import * as React from 'react';

import { Badge, MultiSelectFilter } from '@rematter/ui-web';
import { useAtom } from 'jotai';

import { $columnSelectedSupplierIds } from '../../../_state/transactions.state';
import { useTranslations } from '../../../i18n/useTranslations';

import { FilterOptionLabel } from './FilterOptionLabel';

import {
  SelectableSupplierAccount,
  useSupplierAccountsQuery,
} from '~/shared/components/select-fields/SelectSupplierAccount';

interface InboundSupplierColumnFilterProps {
  onClose?: () => void;
}

//
// ⚛️ ⚛️ ⚛️ INBOUND SUPPLIER COLUMN FILTER ⚛️ ⚛️ ⚛️
//
export const InboundSupplierColumnFilter: React.FC<
  InboundSupplierColumnFilterProps
> = ({ onClose }) => {
  const t = useTranslations();

  // States
  const [searchTerm, setSearchTerm] = React.useState<string | undefined>(
    undefined,
  );

  // Atoms
  const [selectedSupplierIdsSet, setSelectedSupplierIdsSet] = useAtom(
    $columnSelectedSupplierIds,
  );
  const selectedSupplierIds = Array.from(selectedSupplierIdsSet);

  // Memoized values
  const selectedKeys = React.useMemo(
    () => new Set<React.Key>(selectedSupplierIds),
    [selectedSupplierIds],
  );

  // Data hooks
  const {
    data: suppliers,
    isFetching: isFetchingSuppliers,
    isLoading: isLoadingSuppliers,
  } = useSupplierAccountsQuery({
    searchTerm,
  });

  const { data: selectedSuppliers } = useSupplierAccountsQuery({
    ids: selectedSupplierIds,
  });

  // Callbacks
  const handleSearch = React.useCallback((query: string) => {
    setSearchTerm(query);
  }, []);

  const handleSelectionChange = React.useCallback(
    (keys: Set<React.Key>) => {
      setSelectedSupplierIdsSet(new Set(Array.from(keys) as string[]));
    },
    [setSelectedSupplierIdsSet],
  );

  const handleReset = React.useCallback(() => {
    setSelectedSupplierIdsSet(new Set());
  }, [setSelectedSupplierIdsSet]);

  const getLabel = React.useCallback(
    (item: SelectableSupplierAccount, isSelected: boolean) => (
      <FilterOptionLabel
        label={item.name}
        badge={
          item.type && (
            <Badge color={item.type === 'Individual' ? 'warning' : 'primary'}>
              {item.type}
            </Badge>
          )
        }
        isSelected={isSelected}
      />
    ),
    [],
  );

  return (
    <MultiSelectFilter
      className="@InboundSupplierColumnFilter min-w-[350px]"
      entries={suppliers}
      selectedEntries={selectedSuppliers}
      selectedKeys={selectedKeys}
      onSelectionChange={handleSelectionChange}
      getEntryKey={(supplier) => supplier.id}
      getEntryLabel={getLabel}
      onSearch={handleSearch}
      onDismiss={onClose}
      onReset={handleReset}
      searchPlaceholder={t.transactionsView.filters.supplier.searchPlaceholder}
      isLoading={isLoadingSuppliers}
      isFetching={isFetchingSuppliers}
    />
  );
};
